import { ExternalLink, Flame, ScrollText, Shield, Tent, TreePine } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { UiLang, ProtectedArea } from "../lib/types";

interface Props {
  areas: ProtectedArea[];
  lang: UiLang;
}

/**
 * Vernet natur ved stoppet, og reglene som gjelder når man er der.
 *
 * To ting som ser like ut, men kommer fra helt ulike steder og derfor er
 * behandlet ulikt:
 *
 *   Verneområdene er data. De hentes fra Miljødirektoratet, og appen gjengir
 *   dem uten å tolke: navn, verneform, forvaltningsmyndighet, og en lenke til
 *   selve verneforskriften. Hva man får gjøre i Hardangervidda nasjonalpark
 *   står i forskriften for Hardangervidda, ikke i en generell setning — og en
 *   app som oppsummerer den ville tatt på seg et ansvar den ikke kan bære.
 *
 *   Allemannsretten er lov. Bålforbudet fra 15. april til 15. september,
 *   femti meter fra hus når man telter, to netter på samme sted i utmark: det
 *   er friluftsloven og brannforskriften, likt i hele landet. Det er skrevet
 *   inn i appen framfor hentet, fordi det ikke finnes noe API for en lov — og
 *   fordi det er nettopp de tre reglene turister ikke kjenner.
 */
export function ProtectedAreaPanel({ areas, lang }: Props) {
  const t = dictionaries[lang];
  if (areas.length === 0) return null;

  return (
    <div className="protected">
      <ul className="protected-areas">
        {areas.map((area) => (
          <li className="protected-area" key={area.name}>
            <span className="protected-kind">
              <Shield size={10} />
              {t.protectedKind[area.kind] ?? area.kind}
            </span>
            <span className="protected-body">
              <b>{area.name}</b>
              {area.authority && <small>{area.authority}</small>}
              <span className="protected-links">
                {/* Forskriften først: den er det som faktisk gjelder der. */}
                {area.regulation && (
                  <a href={area.regulation} target="_blank" rel="noreferrer">
                    <ScrollText size={10} />
                    {t.protectedRegulation}
                  </a>
                )}
                {area.factSheet && (
                  <a href={area.factSheet} target="_blank" rel="noreferrer">
                    {t.protectedFactSheet} <ExternalLink size={9} />
                  </a>
                )}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/*
        Allemannsretten. Lov, ikke data — og de tre reglene tilhørende turister
        oftest ikke kjenner: bålforbudet, teltavstanden og to-netters-regelen.
      */}
      <ul className="access-rules">
        <li>
          <Flame size={11} />
          <span>{t.accessFire}</span>
        </li>
        <li>
          <Tent size={11} />
          <span>{t.accessTent}</span>
        </li>
        <li>
          <TreePine size={11} />
          <span>{t.accessLeaveNoTrace}</span>
        </li>
      </ul>

      <p className="protected-credit">{t.protectedCredit}</p>
    </div>
  );
}
